import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, User as UserIcon, Save, Trash2, Plug, X, ExternalLink, Shield, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import ChangePasswordCard from "@/components/settings/ChangePasswordCard";
import PersonalInfoPanel from "./PersonalInfoPanel";

const MAX_AVATAR_BYTES = 1_500_000; // ~1.5 MB raw bytes before base64 inflation

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", title: "", bio: "", timezone: "", avatar_data_url: "" });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(true);
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/profile");
      setForm({
        name: data.name || "",
        title: data.title || "",
        bio: data.bio || "",
        timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        avatar_data_url: data.avatar_data_url || "",
      });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Avatar must be an image");
      return;
    }
    if (f.size > MAX_AVATAR_BYTES) {
      toast.error("Avatar must be under 1.5 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((s) => ({ ...s, avatar_data_url: reader.result }));
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me/profile", form);
      toast.success("Profile saved");
      await refresh();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Save failed"));
    } finally { setSaving(false); }
  };

  const clearAvatar = () => setForm((s) => ({ ...s, avatar_data_url: "" }));

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="My profile"
        description="Your public-facing name, role, and avatar. Used across mentions, comments, and meetings."
        testid="profile-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 max-w-3xl">
        {busy ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <form onSubmit={save} className="space-y-6" data-testid="profile-form">
            <section className="border border-border p-5 flex items-center gap-5">
              <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0 border border-border" data-testid="profile-avatar-preview">
                {form.avatar_data_url ? (
                  <img src={form.avatar_data_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon className="w-8 h-8 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-lg truncate" style={{ fontFamily: "var(--font-heading)" }}>{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                <div className="flex gap-2 mt-3">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickAvatar} data-testid="profile-avatar-input" />
                  <Button type="button" variant="outline" className="rounded-none h-8 text-xs" onClick={() => fileRef.current?.click()} data-testid="profile-avatar-upload">
                    <Upload className="w-3 h-3 mr-1" /> Upload
                  </Button>
                  {form.avatar_data_url && (
                    <Button type="button" variant="ghost" className="rounded-none h-8 text-xs" onClick={clearAvatar} data-testid="profile-avatar-clear">
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">PNG, JPG, WEBP up to 1.5 MB.</p>
              </div>
            </section>

            <section className="border border-border p-5 space-y-4">
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Display name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Role / title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Senior Designer, Project Manager, Founder…" data-testid="profile-title" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Bio</Label>
                <Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="A short bio that teammates and clients see on hover." data-testid="profile-bio" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Timezone</Label>
                <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="Asia/Kolkata" data-testid="profile-timezone" />
                <p className="text-[10px] text-muted-foreground mt-1">Used when scheduling meetings and rendering due dates.</p>
              </div>
            </section>

            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="rounded-none" data-testid="profile-save">
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>
        )}
        <ConnectedAccountsCard />
        {/* Iter 253c · Personal info edits with HR approval + address geo */}
        <PersonalInfoPanel />
        {/* Iter 248 · Change-your-own-password lives in My Profile per user request. */}
        <div className="mt-6">
          <ChangePasswordCard />
        </div>
        {/* Iter 253z-13 · MyPermissionsPanel + CelebrationsPreferences hidden
            per product decision — kept in code for future re-enable. */}
        {/* <MyPermissionsPanel /> */}
        {/* <CelebrationsPreferences /> */}
      </div>
    </>
  );
}

function CelebrationsPreferences() {
  const { activeWorkspaceId } = useAuth();
  const [optOut, setOptOut] = useState(false);
  const [today, setToday] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspaceId}/celebrations/me`);
        setToday(data.mine || []);
        setLoaded(true);
      } catch { setLoaded(true); }
    })();
  }, [activeWorkspaceId]);
  const save = async (v) => {
    setOptOut(v);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/celebrations/preferences`, { celebrations_opt_out: v });
      toast.success(v ? "Fanfare turned off" : "Fanfare turned back on");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save")); }
  };
  if (!loaded) return null;
  return (
    <section className="border border-border p-5 mt-6 space-y-3" data-testid="profile-celebrations">
      <div className="label-eyebrow">Celebrations</div>
      <p className="text-sm text-muted-foreground">
        Your workspace celebrates birthdays and work anniversaries automatically. Want to skip the fanfare on your day? Toggle below — you&apos;ll still see everyone else&apos;s celebrations.
      </p>
      <label className="flex items-center gap-3 py-1">
        <input type="checkbox" checked={optOut} onChange={(e) => save(e.target.checked)} data-testid="profile-celebrations-optout" />
        <span className="text-sm">Skip fanfare on my celebrations (Focus mode)</span>
      </label>
      {today.length > 0 && !optOut && (
        <div className="text-xs text-muted-foreground border-l-2 border-amber-400 pl-2">
          🎉 You&apos;re celebrated today — {today.map((t) => t.kind.replace(/_/g, " ")).join(", ")}.
        </div>
      )}
      <p className="text-[11px] text-muted-foreground">
        Your birthday, joining date and wedding anniversary are set by HR from the People module.
      </p>
    </section>
  );
}

function ConnectedAccountsCard() {
  const { activeWorkspaceId } = useAuth();
  const [conn, setConn] = useState(null);
  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/oauth/google/status`);
      setConn(data);
    } catch { setConn({ connected: false }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);
  useEffect(() => {
    const handler = (evt) => { if (evt.data?.type === "acquilix_oauth") load(); };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line
  }, [activeWorkspaceId]);

  const connect = async () => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/oauth/google/start`);
      window.open(data.url, "acquilix-oauth", "width=520,height=680");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Ask your workspace owner to configure Google integration first.");
    }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google account?")) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/oauth/google/disconnect`);
      toast.success("Disconnected");
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Disconnect failed"); }
  };

  if (!conn) return null;
  return (
    <section className="border border-border p-5 mt-6 space-y-3" data-testid="profile-connected-accounts">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-eyebrow">Connected accounts</div>
          <p className="text-sm text-muted-foreground mt-1">
            Send emails and sync calendars using your own Google account. Manage per-workspace in{" "}
            <Link to="/app/integrations" className="underline">Integrations <ExternalLink className="inline w-3 h-3" /></Link>.
          </p>
        </div>
      </div>
      <div className="border border-border bg-muted/10 p-3 flex items-center gap-3 flex-wrap" data-testid="profile-google-row">
        <div className="w-8 h-8 border border-border bg-white flex items-center justify-center font-bold">
          <span style={{ background: "linear-gradient(90deg,#4285F4,#EA4335,#FBBC05,#34A853)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>G</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Google (Gmail + Calendar)</div>
          {conn.connected ? (
            <div className="text-xs text-muted-foreground truncate">{conn.account_email}</div>
          ) : (
            <div className="text-xs text-muted-foreground">Not connected</div>
          )}
        </div>
        {conn.connected ? (
          <Button variant="ghost" className="rounded-none h-8 text-red-600 hover:text-red-700" onClick={disconnect} data-testid="profile-google-disconnect">
            <X className="w-3.5 h-3.5 mr-1" /> Disconnect
          </Button>
        ) : (
          <Button className="rounded-none h-8" onClick={connect} data-testid="profile-google-connect">
            <Plug className="w-3.5 h-3.5 mr-1" /> Connect
          </Button>
        )}
      </div>
    </section>
  );
}


function MyPermissionsPanel() {
  const { activeWorkspaceId, activeWorkspace } = useAuth();
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [roleInfo, setRoleInfo] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const [permsRes, catRes] = await Promise.all([
          api.get(`/me/permissions/${activeWorkspaceId}`),
          api.get(`/permissions`),
        ]);
        if (cancelled) return;
        setData(permsRes.data);
        setCatalog(catRes.data?.groups || []);
        // Prefer role info returned by /me/permissions (works for custom-role users too).
        const roleFromApi = permsRes.data?.role;
        if (roleFromApi?.slug) {
          setRoleInfo(roleFromApi);
        } else {
          const mySlug = activeWorkspace?.role;
          if (mySlug) {
            try {
              const rolesRes = await api.get(`/workspaces/${activeWorkspaceId}/roles`);
              const found = (rolesRes.data || []).find((r) => r.slug === mySlug);
              setRoleInfo(found || { slug: mySlug, name: mySlug });
            } catch {
              setRoleInfo({ slug: mySlug, name: mySlug });
            }
          }
        }
      } catch {
        if (!cancelled) setData({ permissions: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [activeWorkspaceId, activeWorkspace?.role]);

  if (!activeWorkspaceId) return null;
  const perms = new Set(data?.permissions || []);
  const total = perms.size;
  const qq = q.trim().toLowerCase();

  return (
    <section className="border border-border p-5 mt-6 space-y-3" data-testid="profile-my-permissions">
      <div className="flex items-start gap-3">
        <Shield className="w-4 h-4 mt-0.5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow">My access</div>
          <p className="text-sm text-muted-foreground mt-1">
            Your effective permissions in <strong>{activeWorkspace?.name || "this workspace"}</strong>.
            {roleInfo?.name && (
              <> Role: <span className="chip text-[10px] ml-1" data-testid="profile-my-role">{roleInfo.name}</span></>
            )}
            {roleInfo?.slug && roleInfo.slug !== roleInfo.name && (
              <code className="text-[10px] text-foreground/50 font-mono ml-1">{roleInfo.slug}</code>
            )}
          </p>
        </div>
        <Button type="button" variant="outline" className="rounded-none h-8 text-xs" onClick={() => setExpanded((v) => !v)} data-testid="profile-my-perms-toggle">
          {expanded ? "Hide" : "Show"} ({total})
        </Button>
      </div>

      {expanded && (
        <div className="space-y-3">
          <div className="relative max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <Input
              className="pl-8 h-9 text-xs"
              placeholder="Search permissions…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="profile-my-perms-search"
            />
          </div>
          {total === 0 && (
            <div className="text-xs text-foreground/60 border border-dashed border-border p-3" data-testid="profile-my-perms-empty">
              You have no explicit permissions in this workspace. Ask an admin to assign a role.
            </div>
          )}
          {catalog.map((g) => {
            const granted = g.permissions.filter((p) => perms.has(p.code));
            const visible = qq
              ? granted.filter((p) => p.label.toLowerCase().includes(qq) || p.code.toLowerCase().includes(qq))
              : granted;
            if (granted.length === 0) return null;
            if (qq && visible.length === 0) return null;
            return (
              <div key={g.label} className="border border-border p-3" data-testid={`profile-my-perms-group-${g.label}`}>
                <div className="font-semibold text-sm mb-1">
                  {g.label} <span className="text-[11px] text-foreground/50 ml-2">{visible.length}/{g.permissions.length}</span>
                </div>
                <ul className="text-xs space-y-0.5">
                  {visible.map((p) => (
                    <li key={p.code} className="flex items-start gap-2" data-testid={`profile-my-perm-${p.code}`}>
                      <span className="w-1 h-1 bg-primary rounded-full mt-1.5 shrink-0" />
                      <span>{p.label} <code className="text-[10px] text-foreground/50 font-mono ml-1">{p.code}</code></span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          {qq && total > 0 && catalog.every((g) => {
            const granted = g.permissions.filter((p) => perms.has(p.code));
            const visible = granted.filter((p) => p.label.toLowerCase().includes(qq) || p.code.toLowerCase().includes(qq));
            return visible.length === 0;
          }) && (
            <div className="text-xs text-foreground/60 border border-dashed border-border p-3" data-testid="profile-my-perms-no-match">
              No permission matches &ldquo;{q}&rdquo;.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
